import { useState } from "react";
import { Notes, Order } from "@/types/my-tasks";
import { styled } from "@stitches/react";
import { Table, Button, Textarea } from "@/vendor/Forge/dist";
import { useNotes, useNotesMutation } from "@/hooks/use-notes";
import Loader from "@/components/Loader";
import { useAppState } from "@contexts/app-state";
import { capitalize } from "@/utils/helpers";

const Wrapper = styled("div", {
  minHeight: 300,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "& table.fe_c_table > thead > tr > th": {
    paddingLeft: 0,
  },
  "& table.fe_c_table > tbody > tr > td.fe_c_table__cell": {
    paddingLeft: 0,
  },
});

const FormWrapper = styled("div", {
  marginTop: 10,
  display: "flex",
  alignItems: "center",
  gap: 12,
  "& > button": {
    width: 135,
  },
});

const TableWrapper = styled("div", {
  maxHeight: 350,
  overflowY: "scroll",
  display: "flex",
  flexDirection: "column",
});

type NotesKey = keyof Notes;

interface Column {
  key: NotesKey;
  displayName: string;
}

const columns: Column[] = [
  {
    key: "timeStamp",
    displayName: "Time Stamp",
  },
  {
    key: "status",
    displayName: "Status",
  },
  {
    key: "createdUser",
    displayName: "User",
  },
  {
    key: "actionNote",
    displayName: "Action Note",
  },
];

interface Props {
  order: Order;
}

const LatestNotes = ({ order }: Props) => {
  const [note, setNote] = useState("");
  const {
    data: latestNotes = [],
    isPending: isFetchingNotes,
    isError,
    error,
    refetch,
  } = useNotes(order.documentId);
  const { isPending: isCreatingNotes, createNote } = useNotesMutation();

  const { loggedInUser } = useAppState();

  const handleAddNoteClick = () => {
    createNote({
      orderIds: [order.documentId],
      actionNote: `${capitalize(loggedInUser)} noted: ${note}`,
    },
    {
      onSettled: async () => {
        await refetch();
      }
    });
    setNote("");
  };

  return (
    <Wrapper>
      <Loader
        isLoading={isFetchingNotes}
        displayText="Loading Notes..."
        isError={isError}
        errorReason={error?.message}
      >
        <TableWrapper>
          <Table
            nowrapHeaders
            layout="compact"
            rowKey="timeStamp"
            columns={columns}
            rows={latestNotes}
          />
        </TableWrapper>
        <FormWrapper>
          <Textarea
            placeholder="Write your note here..."
            value={note}
            disabled={isCreatingNotes}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNote(e.target.value)
            }
          />
          <Button
            disabled={!note || isCreatingNotes}
            type="button"
            size="small"
            onClick={handleAddNoteClick}
          >
            {isCreatingNotes ? "Adding Notes..." : "Add Note"}
          </Button>
        </FormWrapper>
      </Loader>
    </Wrapper>
  );
};

export default LatestNotes;
